import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Title3,
} from "@fluentui/react-components";
import { Add24Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { getTasksBySpreadsheet, TaskDto } from "../../services/tasks.service";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { useCloseOnNavigation } from "../../hooks/useCloseOnNavigation";
import { useFetch } from "../../hooks/useFetch";
import TaskStatistics from "./components/TaskStatistics";
import TaskFilters from "./components/TaskFilters";
import TaskCard from "./components/TaskCard";
import TaskEmptyState from "./components/TaskEmptyState";
import CreateTaskDialog from "./components/CreateTaskDialog";
import TaskDetailDialog from "./components/TaskDetailDialog";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  taskListContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
  taskList: {
    display: "flex",
    flexDirection: "column",
  },
});

/**
 * AI task list — statistics, filters, manual + 5-second silent refresh,
 * and create/detail dialogs.
 */
const Tasks: React.FC = () => {
  const styles = useStyles();
  const { spreadsheetId } = useWorkflowIntelligence();

  // Data
  const {
    data: tasksData,
    isLoading,
    error,
    refetch: refetchTasks,
    setData: setTasks,
  } = useFetch(
    () => getTasksBySpreadsheet(spreadsheetId!),
    [spreadsheetId],
    { enabled: !!spreadsheetId, transformError: getUserFriendlyErrorMessage }
  );
  const tasks = tasksData ?? [];

  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskDto | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [outputTypeFilter, setOutputTypeFilter] = useState("all");
  const [taskTypeFilter, setTaskTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // Silent auto-refresh — updates the task list every 5s without touching loading
  // state, so the user sees status badges change without flicker or spinners.
  const silentFetch = useCallback(async () => {
    if (!spreadsheetId) return;
    try {
      const data = await getTasksBySpreadsheet(spreadsheetId);
      setTasks(data);
    } catch (err) {
      // Swallow — manual refresh button still surfaces errors.
      console.warn("Silent task refresh failed:", err);
    }
  }, [spreadsheetId, setTasks]);

  useEffect(() => {
    if (!spreadsheetId) return undefined;
    const interval = setInterval(silentFetch, 5000);
    return () => clearInterval(interval);
  }, [spreadsheetId, silentFetch]);

  useCloseOnNavigation(() => {
    setIsDialogOpen(false);
    setIsDetailDialogOpen(false);
  });

  // Extract unique output types and task types for filter dropdowns
  const availableOutputTypes = useMemo(() => {
    const types = new Set<string>();
    tasks.forEach((t) => {
      if (t.outputType) types.add(t.outputType);
    });
    return Array.from(types).sort();
  }, [tasks]);

  const availableTaskTypes = useMemo(() => {
    const types = new Set<string>();
    tasks.forEach((t) => {
      if (t.taskType) types.add(t.taskType);
    });
    return Array.from(types).sort();
  }, [tasks]);

  // Apply filters and sorting
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (t) => t.name.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((t) => t.status === statusFilter);
    }

    // Output type filter
    if (outputTypeFilter !== "all") {
      result = result.filter((t) => t.outputType === outputTypeFilter);
    }

    // Task type filter
    if (taskTypeFilter !== "all") {
      result = result.filter((t) => t.taskType === taskTypeFilter);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
          );
        case "oldest":
          return (
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime()
          );
        case "updated":
          return (
            new Date(b.updatedAt || 0).getTime() -
            new Date(a.updatedAt || 0).getTime()
          );
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return result;
  }, [tasks, search, statusFilter, outputTypeFilter, taskTypeFilter, sortBy]);

  // Calculate statistics
  const statistics = useMemo(() => {
    return {
      toDoCount: tasks.filter((t) => t.status === "to_do").length,
      inProgressCount: tasks.filter((t) => t.status === "in_progress").length,
      incompleteCount: tasks.filter((t) => t.status === "incomplete").length,
      completeCount: tasks.filter((t) => t.status === "complete").length,
    };
  }, [tasks]);

  // Handlers
  const handleTaskClick = (task: TaskDto) => {
    setSelectedTask(task);
    setIsDetailDialogOpen(true);
  };

  const handleDetailDialogDismiss = () => {
    setIsDetailDialogOpen(false);
    setSelectedTask(null);
  };

  const handleCreateTask = () => {
    setIsDialogOpen(true);
  };

  const handleDialogDismiss = () => {
    setIsDialogOpen(false);
  };

  const handleDialogSuccess = () => {
    refetchTasks();
  };

  // Manual refresh uses setTasks directly (not refetch) to avoid the page-level
  // spinner — the refresh button has its own isRefreshing indicator instead.
  const handleRefresh = async () => {
    if (!spreadsheetId || isRefreshing) return;

    setIsRefreshing(true);
    try {
      const data = await getTasksBySpreadsheet(spreadsheetId);
      setTasks(data);
    } catch (err) {
      console.error("Error refreshing tasks:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setOutputTypeFilter("all");
    setTaskTypeFilter("all");
  };

  const hasFilters =
    search !== "" ||
    statusFilter !== "all" ||
    outputTypeFilter !== "all" ||
    taskTypeFilter !== "all";

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Loading agents..." />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text weight="semibold">Error loading agents</Text>
          <Text>{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <Title3>Agents</Title3>
          </div>
          <Button
            appearance="primary"
            icon={<Add24Regular />}
            onClick={handleCreateTask}
          >
            Run Agent
          </Button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <TaskStatistics
        toDoCount={statistics.toDoCount}
        inProgressCount={statistics.inProgressCount}
        incompleteCount={statistics.incompleteCount}
        completeCount={statistics.completeCount}
        onStatusClick={setStatusFilter}
      />

      {/* Filters */}
      <TaskFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        outputTypeFilter={outputTypeFilter}
        onOutputTypeFilterChange={setOutputTypeFilter}
        taskTypeFilter={taskTypeFilter}
        onTaskTypeFilterChange={setTaskTypeFilter}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        resultCount={filteredTasks.length}
        onClearFilters={handleClearFilters}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        availableOutputTypes={availableOutputTypes}
        availableTaskTypes={availableTaskTypes}
      />

      {/* Task List or Empty State */}
      <div className={styles.taskListContainer}>
        {filteredTasks.length === 0 ? (
          <TaskEmptyState
            hasFilters={hasFilters}
            onCreateClick={!hasFilters ? handleCreateTask : undefined}
          />
        ) : (
          <div className={styles.taskList}>
            {filteredTasks.map((task) => (
              <TaskCard key={task.id} task={task} onClick={handleTaskClick} />
            ))}
          </div>
        )}
      </div>

      {/* Create Task Dialog */}
      {spreadsheetId && (
        <CreateTaskDialog
          isOpen={isDialogOpen}
          onDismiss={handleDialogDismiss}
          spreadsheetId={spreadsheetId}
          onSuccess={handleDialogSuccess}
        />
      )}

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask}
        isOpen={isDetailDialogOpen}
        onDismiss={handleDetailDialogDismiss}
      />
    </div>
  );
};

export default Tasks;
